import { matchRoutes, type RouteObject } from 'react-router-dom';

/**
 * Per-page hint registry — the single source of truth for "what is this page
 * about". Every admin route maps to an i18n key (`admin:hints.*`); `AdminShell`
 * renders the matching hint in the ⓘ popover next to the breadcrumb.
 *
 * Resolution uses React Router's own `matchRoutes` ranking, so a specific
 * detail route (`/venues/:id`) and a sibling static route
 * (`/venues/submissions`) resolve exactly the way the real `<Routes>` tree in
 * App.tsx does — no manual ordering required.
 *
 * Keep this in sync with App.tsx: add a new admin route → add its pattern + key
 * here and the copy under `hints` in `i18n/locales/en/admin.json`. A route with
 * no entry simply renders no ⓘ (graceful), but the goal is 100% coverage.
 */
export interface PageHint {
  /** React Router path pattern — must match the one declared in App.tsx. */
  readonly path: string;
  /** Full i18n key resolving to the one-line hint sentence. */
  readonly key: string;
}

export const PAGE_HINTS: readonly PageHint[] = [
  // ── Dashboard ──
  { path: '/', key: 'admin:hints.dashboard' },

  // ── Venues (F10 / F42) ──
  { path: '/venues', key: 'admin:hints.venues' },
  { path: '/venues/new', key: 'admin:hints.venueNew' },
  { path: '/venues/submissions', key: 'admin:hints.venueSubmissions' },
  { path: '/venues/submissions/:id', key: 'admin:hints.venueSubmissionDetail' },
  { path: '/venues/edits', key: 'admin:hints.venueEdits' },
  { path: '/venues/edits/:id', key: 'admin:hints.venueEditDetail' },
  { path: '/venues/merge', key: 'admin:hints.venueMerge' },
  { path: '/venues/merge/:canonicalId/:sourceId', key: 'admin:hints.venueMerge' },
  { path: '/venues/:venueId/payment-config', key: 'admin:hints.venuePaymentConfig' },
  {
    path: '/venues/:venueId/payment-config/override',
    key: 'admin:hints.venuePaymentConfigOverride',
  },
  { path: '/venues/:id', key: 'admin:hints.venueDetail' },

  // ── Users + moderation (F43) ──
  { path: '/users', key: 'admin:hints.users' },
  { path: '/users/:id', key: 'admin:hints.userDetail' },
  // Legacy v1 privileged-issue URL — redirects to /verifications/direct.
  { path: '/users/:userId/verify', key: 'admin:hints.issueVerification' },
  { path: '/users/:userId/skill-ratings', key: 'admin:hints.userSkillRatings' },
  { path: '/reports', key: 'admin:hints.reports' },
  { path: '/reports/:id', key: 'admin:hints.reportDetail' },
  { path: '/gdpr', key: 'admin:hints.gdpr' },

  // ── Verifications (F09 v2) ──
  { path: '/verifications', key: 'admin:hints.verifications' },
  { path: '/verifications/requests/:id', key: 'admin:hints.verificationDetail' },
  { path: '/verifications/entities/:type/:id', key: 'admin:hints.verificationEntity' },
  { path: '/verifications/direct', key: 'admin:hints.verificationDirect' },
  { path: '/verifications/qa', key: 'admin:hints.verificationQa' },
  // Legacy detail URL — redirects to /verifications/requests/:id.
  { path: '/verifications/:requestId', key: 'admin:hints.verificationDetail' },

  // ── Venue claims + B2B (F11) ──
  { path: '/venue-claims', key: 'admin:hints.venueClaims' },
  { path: '/venue-claims/:id', key: 'admin:hints.venueClaimDetail' },
  { path: '/b2b-accounts', key: 'admin:hints.b2bAccounts' },
  { path: '/b2b-accounts/:id', key: 'admin:hints.b2bAccountDetail' },

  // ── Venue photos (F12) ──
  { path: '/venue-photos', key: 'admin:hints.venuePhotos' },
  { path: '/venue-photos/:photoId', key: 'admin:hints.venuePhotoDetail' },

  // ── Bookings (F16 / F17) ──
  { path: '/bookings', key: 'admin:hints.bookings' },
  { path: '/bookings/:id', key: 'admin:hints.bookingDetail' },
  { path: '/cancellation-policy', key: 'admin:hints.cancellationPolicy' },

  // ── Payments (F20 / F23) ──
  { path: '/payment-disputes', key: 'admin:hints.paymentDisputes' },
  { path: '/payment-disputes/:id', key: 'admin:hints.paymentDisputeDetail' },
  { path: '/payments', key: 'admin:hints.payments' },
  { path: '/commission-rates', key: 'admin:hints.commissionRates' },
  { path: '/commission-statements', key: 'admin:hints.commissionStatements' },
  { path: '/commission-statements/:statementId', key: 'admin:hints.commissionStatementDetail' },
  { path: '/financial', key: 'admin:hints.financial' },

  // ── Subscriptions (F38) ──
  { path: '/subscription-tiers', key: 'admin:hints.subscriptionTiers' },
  { path: '/venue-subscriptions', key: 'admin:hints.venueSubscriptions' },
  { path: '/venue-subscriptions/:venueId', key: 'admin:hints.venueSubscriptionDetail' },

  // ── Featured placements (F39) ──
  { path: '/placement-inventory', key: 'admin:hints.placementInventory' },
  { path: '/placement-approvals', key: 'admin:hints.placementApprovals' },
  { path: '/placement-approvals/:id', key: 'admin:hints.placementDetail' },
  { path: '/placements/analytics', key: 'admin:hints.placementAnalytics' },
  { path: '/placements', key: 'admin:hints.placements' },

  // ── Promotions (F40) ──
  { path: '/promotions/analytics', key: 'admin:hints.promotionAnalytics' },
  { path: '/promotions/:id', key: 'admin:hints.promotionDetail' },
  { path: '/promotions', key: 'admin:hints.promotions' },

  // ── Tournaments (F41) ──
  { path: '/tournaments/analytics', key: 'admin:hints.tournamentAnalytics' },
  { path: '/tournaments/:id', key: 'admin:hints.tournamentDetail' },
  { path: '/tournaments', key: 'admin:hints.tournaments' },
  { path: '/organizers', key: 'admin:hints.organizers' },

  // ── Finance dashboard (F44) ──
  { path: '/finance', key: 'admin:hints.finance' },
  { path: '/finance/settlements', key: 'admin:hints.financeSettlements' },
  { path: '/finance/refunds', key: 'admin:hints.financeRefunds' },
  { path: '/finance/exports', key: 'admin:hints.financeExports' },
  { path: '/finance/alert-rules', key: 'admin:hints.financeAlertRules' },

  // ── Analytics (F45) ──
  { path: '/analytics', key: 'admin:hints.analytics' },
  { path: '/analytics/funnels', key: 'admin:hints.analyticsFunnels' },
  { path: '/analytics/retention', key: 'admin:hints.analyticsRetention' },
  { path: '/analytics/geographic', key: 'admin:hints.analyticsGeographic' },
  { path: '/analytics/churn', key: 'admin:hints.analyticsChurn' },
  { path: '/analytics/notifications', key: 'admin:hints.analyticsNotifications' },
  { path: '/analytics/alert-rules', key: 'admin:hints.analyticsAlertRules' },
  { path: '/analytics/scorecards/venues/:id', key: 'admin:hints.analyticsScorecard' },
  { path: '/analytics/scorecards/sports/:id', key: 'admin:hints.analyticsScorecard' },
  { path: '/analytics/scorecards/trainers/:id', key: 'admin:hints.analyticsScorecard' },

  // ── Notification tooling (F26) ──
  { path: '/admin/notifications/dry-run', key: 'admin:hints.notificationsDryRun' },
  { path: '/admin/notifications/test-send', key: 'admin:hints.notificationsTestSend' },

  // ── Email (F28) + WhatsApp (F27) ──
  { path: '/email-suppressions', key: 'admin:hints.emailSuppressions' },
  { path: '/whatsapp/templates', key: 'admin:hints.whatsappTemplates' },
  { path: '/whatsapp/quality', key: 'admin:hints.whatsappQuality' },

  // ── Teams (F29) ──
  { path: '/teams', key: 'admin:hints.teams' },
  { path: '/teams/:teamId', key: 'admin:hints.teamDetail' },

  // ── Skill matching (F31) ──
  { path: '/skill-disputes', key: 'admin:hints.skillDisputes' },
  { path: '/skill-disputes/:id', key: 'admin:hints.skillDisputeDetail' },
  { path: '/sport-skill-descriptors', key: 'admin:hints.sportSkillDescriptors' },

  // ── Open games (F32) ──
  { path: '/open-games', key: 'admin:hints.openGames' },
  { path: '/open-games/:id', key: 'admin:hints.openGameDetail' },
  { path: '/open-game-reports', key: 'admin:hints.openGameReports' },

  // ── Trainers + clubs (F33 / F34 / F35 / F36) ──
  { path: '/trainer-verifications', key: 'admin:hints.trainerVerifications' },
  { path: '/trainer-verifications/:userId', key: 'admin:hints.trainerVerificationDetail' },
  { path: '/trainer-package-consumptions', key: 'admin:hints.trainerPackageConsumptions' },
  { path: '/trainers/:trainerUserId/billing', key: 'admin:hints.trainerBilling' },
  { path: '/clubs', key: 'admin:hints.clubs' },
  { path: '/clubs/:clubId', key: 'admin:hints.clubDetail' },

  // ── Recommendations (F37) ──
  { path: '/recommendation-catalog/badges', key: 'admin:hints.badgeCatalog' },
  { path: '/recommendation-catalog/tags', key: 'admin:hints.reviewTags' },
  { path: '/review-moderation', key: 'admin:hints.reviewModeration' },
  { path: '/review-moderation/:id', key: 'admin:hints.reviewModerationDetail' },

  // ── Internal admin + audit (F06) ──
  { path: '/admin-users', key: 'admin:hints.adminUsers' },
  { path: '/audit-log', key: 'admin:hints.auditLog' },
  // ── Logs & API health (F60) ──
  { path: '/ops/logs', key: 'admin:hints.opsLogs' },
  { path: '/ops/health', key: 'admin:hints.opsHealth' },
  { path: '/account', key: 'admin:hints.account' },
];

const ROUTE_OBJECTS: RouteObject[] = PAGE_HINTS.map((h) => ({ path: h.path }));
const KEY_BY_PATH = new Map<string, string>(PAGE_HINTS.map((h) => [h.path, h.key]));

/**
 * Resolve the hint i18n key for a concrete pathname, or null if none. Uses
 * React Router's `matchRoutes` so specificity matches the live route tree
 * (e.g. `/verifications/abc` → detail hint, not the list hint).
 */
export function resolvePageHintKey(pathname: string): string | null {
  const matches = matchRoutes(ROUTE_OBJECTS, pathname);
  if (!matches || matches.length === 0) return null;
  const matchedPath = matches[matches.length - 1]?.route.path;
  if (matchedPath === undefined) return null;
  return KEY_BY_PATH.get(matchedPath) ?? null;
}
